import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function LinkPage(): Node {
  return html`
    <div>
      <h1>Link Component</h1>
      <p>Declarative navigation with automatic active state. <code>Link</code> renders an anchor that prevents default navigation and uses <code>navigate()</code> internally.</p>

      <h2>Basic Usage</h2>
      ${CodeBlock(`import { Link, html } from 'onefold';

function Nav(): Node {
  return html\`
    <nav>
      \${Link('/', html\`<span>Home</span>\`)}
      \${Link('/about', html\`<span>About</span>\`)}
      \${Link('/contact', html\`<span>Contact</span>\`, 'nav-link')}
    </nav>
  \`;
}`)}

      <h2>Signature</h2>
      ${CodeBlock(`Link(href: string, child: Node, className?: string): Node`)}

      <table>
        <tr><th>Parameter</th><th>Type</th><th>Description</th></tr>
        <tr><td><code>href</code></td><td>string</td><td>Target path for navigation.</td></tr>
        <tr><td><code>child</code></td><td>Node</td><td>Content to render inside the link.</td></tr>
        <tr><td><code>className</code></td><td>string (optional)</td><td>CSS class to apply to the anchor element.</td></tr>
      </table>

      <h2>Active State</h2>
      <p>When the current route matches the link's <code>href</code>, the anchor receives an <code>active</code> class. You can style it with CSS:</p>
      ${CodeBlock(`/* Style active navigation links */
a.active {
  color: var(--primary);
  font-weight: 600;
  border-bottom: 2px solid var(--primary);
}`)}

      ${Callout('Link uses client-side navigation — no full page reload. It calls event.preventDefault() and uses navigate() internally.')}

      <h2>Link vs navigate()</h2>
      <table>
        <tr><th>Use Case</th><th>Approach</th></tr>
        <tr><td>Navigation menus, breadcrumbs</td><td><code>Link()</code> — declarative, accessible</td></tr>
        <tr><td>After form submit, conditional redirect</td><td><code>navigate()</code> — imperative, from code</td></tr>
      </table>

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/routing/navigate">Navigate</a> — programmatic navigation from code</li>
        <li><a href="/routing/params">Dynamic Params</a> — capture URL segments as parameters</li>
      </ul>
    </div>
  `;
}
